// FileUpload — a file picker with click + drag-and-drop, per COMPONENTS
// §Catalog. Native-owned (ADR-028): a real <input type="file"> + a styled label
// wrapper — no focus engine, no React Aria. The upload step of the import wizard
// (ADR-041) and the files library's upload dialog (ADR-068).
//
// TWO SHAPES, one primitive:
//   • SINGLE (default): once a file is selected the dropzone is REPLACED by the
//     selected-file chip (name + remove); the dropzone returns only when the file
//     is removed. Most imports are one file (one sheet).
//   • `multiple`: the dropzone is PERSISTENT — every pick or drop APPENDS to the
//     controlled `files` array and each file gets its own chip + remove. The
//     files library's upload dialog picks several photos at once.
//
// Controlled: the parent owns the selection. Visible strings are props (the
// consumer passes t()'d values; defaults keep it usable in isolation / stories)
// — the primitive is i18n-agnostic, like ConfirmDialog / EmptyState.
//
// a11y: while empty (or always, in multiple mode), the <input> is the focusable
// control (visually minimal, never display:none, so Tab reaches it) and the label
// is its accessible name; a chip's remove button is its own control. Drag-drop is
// a pointer enhancement over the always-present click/keyboard path (WCAG 2.1.1).

import { useId, useState, type DragEvent } from 'react';
import { icons } from './icons.js';
import { IconButton } from './IconButton.js';

interface FileUploadBaseProps {
  /** Comma-separated extensions / MIME types, e.g. '.xlsx,.csv'. Applied to the
   *  native picker and to drop filtering. */
  accept?: string;
  disabled?: boolean;
  /** Primary instruction shown in the dropzone. */
  label?: string;
  /** Secondary hint (accepted formats / size). */
  hint?: string;
  /** Accessible label for a clear button. In multiple mode the file's name is
   *  appended so each chip's control names its file. */
  removeLabel?: string;
  /** Parent-supplied validation message (size/type/server). Rendered as an alert. */
  error?: string;
  id?: string;
  className?: string;
}

export type SingleFileUploadProps = FileUploadBaseProps & {
  multiple?: false;
  /** Controlled selection. */
  file: File | null;
  onFileChange: (file: File | null) => void;
};

export type MultipleFileUploadProps = FileUploadBaseProps & {
  multiple: true;
  /** Controlled selection — every pick / drop APPENDS; a chip's X removes one. */
  files: File[];
  onFilesChange: (files: File[]) => void;
};

export type FileUploadProps = SingleFileUploadProps | MultipleFileUploadProps;

function matchesAccept(file: File, accept?: string): boolean {
  if (!accept) return true;
  const tokens = accept.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (tokens.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return tokens.some((tok) =>
    tok.startsWith('.') ? name.endsWith(tok) : type === tok || (tok.endsWith('/*') && type.startsWith(tok.slice(0, -1))),
  );
}

export function FileUpload(props: FileUploadProps) {
  const {
    accept,
    disabled = false,
    label = 'Drop a file here or browse',
    hint,
    removeLabel = 'Remove file',
    error,
    id,
    className = '',
  } = props;
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const [dragOver, setDragOver] = useState(false);

  const multiple = props.multiple === true;
  const selected: File[] = multiple ? props.files : props.file ? [props.file] : [];

  const accepted = (list: File[]) => list.filter((f) => matchesAccept(f, accept));
  const take = (list: File[]) => {
    if (multiple) {
      const next = accepted(list);
      if (next.length) props.onFilesChange([...props.files, ...next]);
    } else {
      const match = accepted(list)[0];
      if (match) props.onFileChange(match);
    }
  };
  const remove = (file: File) => {
    if (multiple) props.onFilesChange(props.files.filter((f) => f !== file));
    else props.onFileChange(null);
  };

  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    take(Array.from(e.dataTransfer.files));
  };

  const showZone = multiple || selected.length === 0;

  return (
    <div className={className}>
      {/* The dropzone (the click/keyboard/drag target). Single: a selection
          replaces it with the chip; removing the file brings it back. Multiple:
          it stays — the zone is where the next files land. */}
      {showZone && (
        <label
          htmlFor={inputId}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={
            'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-6 py-8 text-center ' +
            'transition-colors focus-within:ring-2 focus-within:ring-[var(--color-primary-ring)] ' +
            (disabled
              ? 'cursor-not-allowed opacity-50 border-[var(--color-border)]'
              : 'cursor-pointer ' +
                (dragOver
                  ? 'border-[var(--color-primary-display)] bg-[var(--color-bg-hover)]'
                  : 'border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]'))
          }
        >
          <icons.upload aria-hidden size={28} className="text-[var(--color-text-secondary)]" />
          <span className="text-sm font-medium text-[var(--color-text-primary)]">{label}</span>
          {hint && <span className="text-xs text-[var(--color-text-secondary)]">{hint}</span>}
          <input
            id={inputId}
            type="file"
            accept={accept}
            multiple={multiple}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className="sr-only"
            onChange={(e) => {
              take(Array.from(e.target.files ?? []));
              // The same file picked twice in a row must fire again (the native
              // input swallows an unchanged value).
              e.target.value = '';
            }}
          />
        </label>
      )}

      {selected.length > 0 && (
        <ul className={`flex flex-col gap-2 ${showZone ? 'mt-3' : ''}`}>
          {selected.map((file, i) => (
            <li
              key={`${file.name}-${file.size}-${i}`}
              className="flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2"
            >
              <icons.file aria-hidden size={18} className="shrink-0 text-[var(--color-text-secondary)]" />
              <span className="flex-1 truncate text-sm text-[var(--color-text-primary)]">{file.name}</span>
              <IconButton
                aria-label={multiple ? `${removeLabel} — ${file.name}` : removeLabel}
                variant="ghost"
                disabled={disabled}
                onClick={() => remove(file)}
              >
                <icons.close aria-hidden size={16} />
              </IconButton>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p id={errorId} role="alert" className="mt-1 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}
    </div>
  );
}
